/**
 * Custom hook that encapsulates a single paginated section of the Devices page.
 *
 * Uses the dedicated `rescueDevices` query which:
 *  - Only returns device-bearing pets (accurate totalCount)
 *  - Uses offset/limit pagination
 *  - Includes transfer details inline (no N+1 enrichment)
 *
 * Transition strategy
 * ───────────────────
 * Apollo's `previousData` keeps the last successful result available when
 * variables change.  We use `data ?? previousData` so the table always has
 * rows to render — old rows stay visible (dimmed via the overlay CSS) while
 * the network request for the new page / search is in flight.
 *
 * `skeletonCount > 0` tells the table component to show loading UI:
 *   • If stale rows exist  →  dim overlay + shimmer  (height stays the same)
 *   • If no rows at all    →  skeleton placeholder rows  (initial load only)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import type { Device } from '../lib/types';
import type { RescueDevicesData, RescueDeviceFilter } from '../lib/rescueQueries';
import { rescueDevicesQuery } from '../lib/rescueQueries';
import { mapRescueDeviceItemToDevice } from '../lib/deviceAdapters';

// ── Hook ────────────────────────────────────

export interface UseRescueDevicesResult {
  /** Device-bearing pets on the current page. */
  devices: Device[];
  /** Accurate count of ALL devices matching this filter (from server). */
  totalCount: number;
  /** Whether there are more pages after the current one. */
  hasMore: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  /**
   * Number of skeleton placeholder rows to show while loading.
   * Based on the previous page's item count so the table maintains its height.
   * Falls back to pageSize on initial load.
   */
  skeletonCount: number;
  error: Error | undefined;
  goNextPage: () => void;
  goPrevPage: () => void;
  goToPage: (page: number) => void;
  resetPage: () => void;
}

/**
 * `searchQuery` is expected to arrive already debounced — the caller owns the delay, since it
 * also drives the un-debounced input value and the `?search=` URL param. Debouncing again in
 * here would stagger this query behind the drained lists on the same page, which read the same
 * value directly: the two would answer for different searches for the length of the extra
 * delay, and a section rendering rows from one under a count from the other disagrees with
 * itself on screen.
 */
export default function useRescueDevices(
  filter: RescueDeviceFilter,
  searchQuery: string,
  pageSize: number,
  resellerName: string,
  completedSince?: string,
  options?: { skip?: boolean },
): UseRescueDevicesResult {
  const skip = options?.skip ?? false;
  // ── Pagination state ──────────────────────

  const [currentPage, setCurrentPage] = useState(1);

  // Reset pagination when search changes
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      prevSearchRef.current = searchQuery;
      setCurrentPage(1);
    }
  }, [searchQuery]);

  // ── Build query variables ─────────────────

  const offset = (currentPage - 1) * pageSize;

  const variables = useMemo(() => {
    const input: Record<string, unknown> = {
      filter,
      limit: pageSize,
      offset,
    };
    if (searchQuery) {
      input.query = searchQuery;
    }
    if (completedSince) {
      input.completedSince = completedSince;
    }
    return { input };
  }, [filter, pageSize, offset, searchQuery, completedSince]);

  // ── GraphQL query ─────────────────────────

  const { data, previousData, loading, error } = useQuery<RescueDevicesData>(rescueDevicesQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip,
  });

  // ── Keep stale rows visible during transitions ──────────
  //
  // When variables change (cache miss), `data` becomes undefined while the
  // network request is in flight.  `previousData` holds the result from the
  // last *successful* query, so `data ?? previousData` always has rows to
  // display — the table never flashes empty.

  const effectiveData = data ?? previousData;
  const result = effectiveData?.rescueDevices;
  const totalCount = result?.totalCount ?? 0;
  const hasMore = result?.hasMore ?? false;

  // ── Map items to Device view-models ───────

  const devices: Device[] = useMemo(() => {
    if (!result?.items) return [];
    return result.items
      .map((item) => mapRescueDeviceItemToDevice(item, resellerName, filter))
      .filter((d): d is Device => d !== null);
  }, [result?.items, resellerName, filter]);

  // ── Skeleton / transition state ───────────────────────────────
  //
  // `skeletonCount > 0` signals the table to show loading UI.
  // The table decides which mode based on whether it has visible rows:
  //   • rows present  → dim overlay with shimmer (preserves table height)
  //   • no rows       → skeleton placeholder bars
  //
  // We are "transitioning" when Apollo is loading and current-variable data
  // hasn't arrived yet (we're showing previousData as a fallback).

  const prevItemCountRef = useRef(pageSize);
  if (devices.length > 0) {
    prevItemCountRef.current = devices.length;
  }

  const isTransitioning = loading && !data;
  const skeletonCount = isTransitioning ? prevItemCountRef.current : 0;

  // ── Computed pagination state ─────────────

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = hasMore;
  const hasPreviousPage = currentPage > 1;

  // ── Navigation handlers ───────────────────

  const goNextPage = useCallback(() => {
    if (hasMore) {
      setCurrentPage((p) => p + 1);
    }
  }, [hasMore]);

  const goPrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(1, p - 1));
  }, []);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(Math.max(1, page));
  }, []);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    devices,
    totalCount,
    hasMore,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    totalPages,
    loading,
    skeletonCount,
    error: error as Error | undefined,
    goNextPage,
    goPrevPage,
    goToPage,
    resetPage,
  };
}
